import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { appConfig } from '@/config/app'
import { modalConfig } from '@/config/modal'
import { locale } from '@/locales'
import {
  APP_CONFIRM_EVENT,
  APP_PROMPT_EVENT,
  APP_TOAST_EVENT,
  type AppConfirmEventPayload,
  type AppPromptEventPayload,
  type AppToastPayload,
  type AppToastTone,
} from '@/utils/uiFeedback'

interface ToastItem {
  id: string
  message: string
  tone: AppToastTone
}

const uiColors = appConfig.ui.colors
const uiFeedback = appConfig.ui.feedback

const toastToneClasses: Record<AppToastTone, string> = {
  info: `${uiColors.surfaceDark} ${uiColors.borderSubtle}`,
  success: `${uiColors.surfaceSuccess} ${uiColors.successBorder}`,
  error: `${uiColors.surfaceError} ${uiColors.errorBorder}`,
}

export const UiFeedbackHost: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [promptRequest, setPromptRequest] = useState<AppPromptEventPayload | null>(null)
  const [confirmRequest, setConfirmRequest] = useState<AppConfirmEventPayload | null>(null)
  const [promptValue, setPromptValue] = useState('')

  const promptTitle = useMemo(
    () => promptRequest?.title || locale.uiFeedback.prompt.defaultTitle,
    [promptRequest],
  )

  const confirmTitle = useMemo(
    () => confirmRequest?.title || locale.uiFeedback.confirm.defaultTitle,
    [confirmRequest],
  )

  useEffect(() => {
    const handleToast = (event: Event) => {
      const detail = (event as CustomEvent<AppToastPayload>).detail
      if (!detail?.message) {
        return
      }

      const nextToast: ToastItem = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        message: detail.message,
        tone: detail.tone || 'info',
      }

      setToasts((prev) => [...prev, nextToast])

      window.setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== nextToast.id))
      }, appConfig.ui.notificationDurationMs)
    }

    const handlePrompt = (event: Event) => {
      const detail = (event as CustomEvent<AppPromptEventPayload>).detail
      if (!detail) {
        return
      }

      setPromptValue(detail.defaultValue || '')
      setPromptRequest(detail)
    }

    const handleConfirm = (event: Event) => {
      const detail = (event as CustomEvent<AppConfirmEventPayload>).detail
      if (!detail) {
        return
      }

      setConfirmRequest(detail)
    }

    window.addEventListener(APP_TOAST_EVENT, handleToast)
    window.addEventListener(APP_PROMPT_EVENT, handlePrompt)
    window.addEventListener(APP_CONFIRM_EVENT, handleConfirm)

    return () => {
      window.removeEventListener(APP_TOAST_EVENT, handleToast)
      window.removeEventListener(APP_PROMPT_EVENT, handlePrompt)
      window.removeEventListener(APP_CONFIRM_EVENT, handleConfirm)
    }
  }, [])

  const closePromptWith = (value: string | null) => {
    if (promptRequest) {
      promptRequest.resolve(value)
    }
    setPromptRequest(null)
    setPromptValue('')
  }

  const closeConfirmWith = (value: boolean) => {
    if (confirmRequest) {
      confirmRequest.resolve(value)
    }
    setConfirmRequest(null)
  }

  return (
    <>
      <div
        aria-live="polite"
        className={`fixed left-1/2 ${uiFeedback.toastStackZIndex} flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4`}
        style={{ bottom: `${appConfig.ui.feedbackBottomPx}px` }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`rounded-xl border px-4 py-3 text-sm ${uiColors.textPrimary} shadow-lg ${toastToneClasses[toast.tone]}`}
          >
            {toast.message}
          </div>
        ))}
      </div>

      <Modal
        isOpen={!!promptRequest}
        onClose={() => closePromptWith(null)}
        maxHeight="fit-content"
        ariaLabel={promptTitle}
        zIndex={modalConfig.layout.baseZIndex + modalConfig.layout.nestedZIndexOffset * 2}
      >
        {promptRequest && (
          <div className={`${uiFeedback.dialogContentPadding} ${uiColors.textPrimary}`}>
            <h3 className="mb-2 text-lg font-bold">{promptTitle}</h3>
            <p className={`mb-4 text-sm ${uiColors.textSecondary}`}>{promptRequest.message}</p>
            <input
              type={promptRequest.inputType || 'text'}
              value={promptValue}
              onChange={(event) => setPromptValue(event.target.value)}
              placeholder={promptRequest.placeholder || ''}
              className={`mb-4 w-full ${uiFeedback.inputRounded} border ${uiColors.borderStrong} ${uiColors.surfaceDark} ${uiFeedback.inputPadding} ${uiColors.textPrimary} outline-none focus:${uiColors.accentBlueBorder}`}
            />
            <div className={`flex items-center ${uiFeedback.actionsGap}`}>
              <button
                type="button"
                onClick={() => closePromptWith(null)}
                className={`flex-1 ${uiFeedback.actionButtonRounded} ${uiColors.surfaceDark} ${uiFeedback.actionButtonPadding} ${uiFeedback.actionButtonText}`}
              >
                {promptRequest.cancelText || locale.common.cancel}
              </button>
              <button
                type="button"
                onClick={() => closePromptWith(promptValue)}
                className={`flex-1 ${uiFeedback.actionButtonRounded} ${uiColors.accentBlueBg} ${uiFeedback.actionButtonPadding} ${uiFeedback.actionButtonText} ${uiColors.textPrimary}`}
              >
                {promptRequest.confirmText || locale.common.confirm}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!confirmRequest}
        onClose={() => closeConfirmWith(false)}
        maxHeight="fit-content"
        ariaLabel={confirmTitle}
        zIndex={modalConfig.layout.baseZIndex + modalConfig.layout.nestedZIndexOffset * 2}
      >
        {confirmRequest && (
          <div className={`${uiFeedback.dialogContentPadding} ${uiColors.textPrimary}`}>
            <h3 className="mb-2 text-lg font-bold">{confirmTitle}</h3>
            <p className={`mb-4 text-sm ${uiColors.textSecondary}`}>{confirmRequest.message}</p>
            <div className={`flex items-center ${uiFeedback.actionsGap}`}>
              <button
                type="button"
                onClick={() => closeConfirmWith(false)}
                className={`flex-1 ${uiFeedback.actionButtonRounded} ${uiColors.surfaceDark} ${uiFeedback.actionButtonPadding} ${uiFeedback.actionButtonText}`}
              >
                {confirmRequest.cancelText || locale.common.cancel}
              </button>
              <button
                type="button"
                onClick={() => closeConfirmWith(true)}
                className={`flex-1 ${uiFeedback.actionButtonRounded} ${uiColors.accentBlueBg} ${uiFeedback.actionButtonPadding} ${uiFeedback.actionButtonText} ${uiColors.textPrimary}`}
              >
                {confirmRequest.confirmText || locale.common.confirm}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}